export default {
  formBuilder: {
    templateLoadingText: 'Please wait... Template is being created',
    exitPromptErrorMessage: 'Any unsaved changes to your form will be lost. Are you sure you want to leave?',
    unsavedChangesModal: {
      title: 'You have unsaved changes',
      body: "Exiting will discard any changes you've made. This cannot be undone.",
      exit: 'Exit',
      continueEditing: 'Continue Editing',
    },
    elementList: {
      searchPlaceholder: 'Search components',
      title: 'Drag or click to add',
      langSelector: {
        title: 'Active Languages',
        languages: {
          en: 'English',
          fr: 'French',
        },
      },
      clearButton: 'Clear',
      clearMessage: 'Are you sure you want to clear the template?',
      saveButton: {
        save: 'Save',
        update: 'Update',
        saveAsCopy: 'Save Copy',
      },
      createdSuccessfully: 'Template created successfully',
      updatedSuccessfully: 'Template updated successfully',
      saveError: 'There was an error processing your template.',
      confirmSaveModal: {
        title: 'Save Template Changes',
        body:
          'This template is currently used in {{count}} position(s). Saving changes will update all associated positions and candidate views immediately. Do you wish to proceed?',
        cancel: 'Cancel',
        proceed: 'Proceed & Save Changes',
      },
    },
    header: {
      templateNameInputPlaceholder: 'Question template name',
    },
    schema: {
      freeform: {
        label: 'Freeform',
        chipLabel: 'Freeform',
      },
      yesno: {
        label: 'Yes/No',
        chipLabel: 'Yes/No',
        branches: ['Yes', 'No'],
      },
      multiChoice: {
        label: 'Multiple choice',
        chipLabel: 'Multiple Choice',
      },
      yesnoWithImage: {
        label: 'Yes/No with Image',
        chipLabel: 'Yes/No',
        branches: ['Yes', 'No'],
      },
      multiChoiceWithImage: {
        label: 'Multiple choice with Image',
        chipLabel: 'Multiple Choice',
      },
      videoResponse: {
        label: 'Asynchronous Video Response',
        chipLabel: 'Asynchronous Video Response',
      },
      documentUpload: {
        label: 'Document Upload',
        chipLabel: 'Document Upload',
      },
    },
    preview: {
      title: 'Preview',
      deviceViewButton: 'Switch to Device View',
      listViewButton: 'Switch to List View',
      listView: {
        noQuestions: 'No questions yet.',
        noQuestionText: 'Untitled Question',
        optionTextPlaceholder: 'Option {{index}}',
        conditionIf: '↳ If "{{condition}}"',
      },
      mobileView: {
        title: 'Job profile',
        headerTitle: 'Parts Manager',
        information: 'INFORMATION',
        messaging: 'MESSAGING',
        welcome: {
          title: 'Application in-progress',
          question:
            'Thank you for starting your application! Please answer the following questions to complete your application.',
        },
        question: {
          defaultTitle: 'Question from the recruiter',
          noQuestionText: 'Untitled Question',
          writeQuestionText: 'Write your answer here',
        },
        mcqSelectSingle: 'Select the best option',
        mcqSelectMultiple: 'Select multiple options',
        optionNoText: 'Option {{index}}',
        documentUpload: {
          info:
            'Tap the button below to upload your document. The file must be in JPG, PNG, JPEG, DOC, or PDF format, under 5 MB, and clearly readable. Only {{count}} file can be uploaded.',
          info_plural:
            'Click the button below to upload your document. The file must be in JPG, PNG, JPEG, DOC or PDF format, under 5 MB, and clearly readable. Only {{count}} files can be uploaded.',
          skipQuestionInfo: 'I don’t have what is being requested',
          upload: 'Upload',
          capture: 'Capture',
        },
        videoResponse: {
          skipQuestionInfo: "I can't provide what is being requested",
          header: 'Video Recording Required',
          instruction:
            'Record a brief video in a quiet, well-lit space. Background noise or poor lighting could lead to a failed assessment. The video cannot be longer than {{count}} max minute(s).',
          questionTitle: 'Answer this question while facing the camera',
          footerNote: 'This question stays visible while you record',
          start: 'Start',
        },
      },
    },
    canvas: {
      emptyContent: 'Click on the items on your left to start building your form.',
      dropMessage: 'INSERT HERE',
      maxDepthError: 'CANNOT DROP HERE: You’ve reached the maximum number of follow-up questions.',
      noDropError: 'CANNOT DROP HERE: You can’t place this item here.',
      element: {
        questionInputPlaceholder: 'Type your question here ({{limit}} characters max)',
        deleteQuestionMessage: 'Are you sure you want to delete the question?',
        addFollowUp: 'Add follow-up question',
        followNextQuestion: 'Show next questions on',
        requireResponse: {
          label: 'Require response for all applications',
          info: 'When a candidate reapplies to this position, ask this question again.',
        },
        screeningTypes: {
          label: 'Screening Type',
          none: 'None',
          knockout: 'Knockout - Automatic rejection on wrong answer',
          review: 'Review - Manual evaluation',
        },
        maxCharactersInputLabel: 'Maximum answer length',
        maxCharactersInputPlaceholder: 'e.g. 50',
        validationRules: {
          title: 'Validation Rules',
          ruleText1: 'When candidate selects',
          ruleText2: 'option, the answer',
          ruleText2_plural: 'options, the answers',
          any: 'any',
          of: 'of',
          selectOptionNoSelection: 'Select options',
          selectOptionWithSelection: 'Options: {{selectedOptions}}',
          operator: {
            IS_EQUAL: { label: 'must be equal to', description: 'Selected options must exactly match' },
            HAS_ANSWER: { label: 'must include', description: 'Selected options must include specified items' },
            DOES_NOT_HAVE_ANSWER: {
              label: 'must not include',
              description: 'Selected options must not include specified items',
            },
            AT_LEAST: {
              label: 'must include at least',
              description: 'Selected options must not include specified items',
            },
          },
          videoResponse: {
            maxVideoLength: 'Max video length:',
            minLabel: 'min',
          },
        },
        documentUpload: {
          supportedTypeTitle: 'Permitted File Types:',
          supportedTypes: 'JPG, PNG, JPEG, DOC, PDF',
          validationRuleText1: 'Candidate must upload ',
          validationRuleText2: 'file. Max file size is 5MB.',
          skipQuestionLabel: 'Candidates have the option to skip',
        },
        options: {
          title: 'Answer options',
          addOption: 'Add option',
          optionTextPlaceholder: 'Enter option text here',
        },
        videoResponse: {
          questionPlaceholder: 'Write your screening question here.',
          questionHint: 'Be clear and conversational — candidates record a video response.',
          maxCharacters: 'Max 300 characters',
        },
      },
    },
    validationErrors: {
      validationRules: {
        noOptionSelected: 'Select at least one correct answer option.',
        isEqual: {
          exactCountRequired: 'Choose exactly 1 correct answer.',
          exactCountRequired_plural: 'Choose exactly {{count}} correct answers.',
          exceedsAvailable: "You've selected more options than the question has. Please remove some.",
        },
        mustInclude: {
          tooManySelected: 'You can mark up to 1 option as correct for this rule.',
          tooManySelected_plural: 'You can mark up to {{count}} options as correct for this rule. Remove some.',
          exceedsAvailable: "You've selected more options than the question has. Please remove some.",
        },
        mustNotInclude: {
          needsMoreOptions: 'This rule needs at least 2 options in the question to work.',
          mustLeaveOneOption: 'At least one option must be left unselected. Lower the selection count or remove an option.',
          tooManySelected: 'You can exclude up to 1 option with this rule.',
          tooManySelected_plural: 'You can exclude up to {{max}} options with this rule. Remove some.',
        },
        atLeast: {
          minCountRequired: 'Set the minimum number of options the candidate must choose.',
          minExceedsSelection:
            "The minimum ({{minCount}}) can't be more than the number of options selected ({{selectionCount}}). Lower the minimum.",
          notEnoughOptionsSelected: 'Select at least 1 correct answer option for this rule.',
          notEnoughOptionsSelected_plural: 'Select at least {{minCount}} correct answer options for this rule.',
        },
      },
      optionEmpty: 'Option text cannot be empty',
      optionRequired: 'Option text is required',
      optionMax: 'The option text must not exceed {{limit}} characters',
      optionMin: 'The option text cannot be empty',
      imageMin: 'Please add at least one image for this question type',
      imageRequired: 'At least one image is required for this question type',
      questionTextEmpty: 'Please enter the question',
      questionTextRequired: 'Question text is required',
      questionTextMax: 'Question text cannot exceed {{limit}} characters',
      questionTextMin: 'Question text cannot be empty',
      maxLengthBase: 'Please enter a valid character limit',
      maxLengthInteger: 'Character limit must be a whole number',
      maxLengthMin: 'Character limit must be at least 1',
      maxLengthMax: 'Character limit cannot exceed {{limit}}',
      maxLengthRequired: 'Character limit is required for free form questions',
      templateNameEmpty: 'Please enter a template name',
      templateNameMin: 'Template name must be at least 1 character',
      templateNameMax: 'Template name cannot exceed {{limit}} characters',
      templateNameRequired: 'Template name is required',
      localeOnly: `Please select a valid question locale`,
      localeRequired: 'Please select a question locale',
      localeEmpty: 'Please select a question locale',
      defaultBase: 'Default must be a boolean',
      defaultRequired: 'Default is required',
      questionsMin: 'Please add at least one question',
      questionsRequired: 'At least one question is required',
    },
  },
  modal: {
    screeningType: 'Screening Type',
    requireResponseAllApplications: 'Require response for all applications.',
    maxAnswerLength: 'Maximum answer length',
    characters: '{{count}} Character',
    characters_plural: '{{count}} Characters',
    expectedAnswer: 'Expected Answer',
    answerOptions: 'Answer Options',
    candidatesCanSkip: 'Candidates have the option to skip this question.',
  },

  validationMessages: {
    isEqual:
      'Candidates need to select <bold>{{total}}</bold> {{totalOptionLabel}}, The answers <bold>must be equal to</bold> {{optionLabel}}: <bold>{{letters}}</bold>',
    hasAnswer:
      'Candidates need to select <bold>{{total}}</bold> {{totalOptionLabel}}, The answers <bold>must include</bold> {{optionLabel}}: <bold>{{letters}}</bold>',
    doesNotHaveAnswer:
      'Candidates need to select <bold>{{total}}</bold> {{totalOptionLabel}}, The answers <bold>must not include</bold> {{optionLabel}}: <bold>{{letters}}</bold>',
    atLeast:
      'Candidates need to select <bold>{{total}}</bold> {{totalOptionLabel}}, The answers <bold>must include at least {{min}} </bold>of {{optionLabel}}: <bold>{{letters}}</bold>',
    option: 'Option',
    options: 'Options',
  },
};
